import { all, put, takeEvery, select, take, call } from 'redux-saga/effects';
import { initialize, getFormNames, stopSubmit, reset } from 'redux-form';

import {
  IDENTITY_VERIFICATION_FORM,
  ADDITIONAL_IDENTITY_VERIFICATION_FORM,
  ACCOUNT_SETTINGS_FORM,
  VERIFICATION_AWAITING_MODAL,
} from '../constants';
import { getSecretQuestions } from 'helpers/index';
import {
  getCurrencyByCountry,
  defaultLeverage,
  defaultPlatform,
  getUser,
  getUserLiveAccountTypes,
} from 'modules/auth/selectors';

import {
  getActiveStep,
  getClassicVerification,
  getPersonalDetailsVerification,
  getSettingsVerification,
  getClassicVerificationSecondStep,
  getKeepCurrentStep,
} from '../selectors';

import {
  initializePersonalDetails,
  initializeSettingsVerification,
  initializeClassicVerification,
  initializeClassicVerificationSecondStep,
  getVerificationUserRequest,
  updatePersonalDetailsSuccess,
  updatePersonalDetailsFail,
  changeStepConfirmed,
  getVerificationUserSuccess,
  settingsVerificationUpdateSuccess,
  classicVerificationUpdateSuccess,
  classicVerificationSecondStepUpdateSuccess,
  settingsVerificationUpdateFail,
  classicVerificationUpdateFail,
  classicVerificationSecondStepUpdateFail,
  verificationPageOpened,
  changeStepForce,
} from '../actions';

import * as geolocationActions from 'modules/geolocation/actions';

import formatFormData from '../../../components/PersonalDetailsForm/helpers/formatFormData';
import { getFormattedCountries } from 'modules/geolocation/selectors';
import * as notificationsActions from 'modules/notifications/actions';
import { PERSONAL_DETAILS_FORM } from '../../../components/PersonalDetailsForm/constants';
import { PROFILE_DETAILS_FORM } from '../../ProfileData/constants';
import get from 'lodash/get';
import {
  MFA_TYPE,
  VERIFICATION_PHONE_NUMBER_FORM,
  VERIFICATION_PHONE_NUMBER_MODAL,
} from 'modules/auth/constants';
import { closeModal, openModal } from 'components/TP-UI/TPModal/actions';
import {
  verifyPhoneNumberSuccess,
  verifyPhoneNumberFail,
  cancelSetupPhoneNumber,
} from 'modules/auth/actions';

function* handleUpdatePersonalDetailsSuccess(action) {
  const {
    profile: { phoneVerified, phoneNumber } = {},
    mfa: { allowedTypes } = {},
  } = action.response.data;
  yield put(
    notificationsActions.showNotificationInfo({
      key: 'profile:verification.notifications.dataIsSaved',
    }),
  );
  if (!phoneVerified && phoneNumber && allowedTypes?.some((type) => type === MFA_TYPE.SMS)) {
    yield put(openModal(VERIFICATION_PHONE_NUMBER_MODAL));
  }
}

function* handleVerifyPhoneNumberSuccess() {
  yield put(
    notificationsActions.showNotificationInfo({
      key: 'auth:verifyPhone.notifications.success',
    }),
  );
  yield put(reset(VERIFICATION_PHONE_NUMBER_FORM));
  yield put(closeModal(VERIFICATION_PHONE_NUMBER_MODAL));
}

function* handleVerifyPhoneNumberFail(action) {
  const { message: errorMessage } = get(action, 'error.data', {});

  yield put(
    stopSubmit(VERIFICATION_PHONE_NUMBER_FORM, {
      _error: errorMessage || {
        key: 'backend:somethingWrongContactHelp',
      },
    }),
  );
}

function* handleSettingsVerificationUpdateSuccess() {
  yield put(
    notificationsActions.showNotificationInfo({
      key: 'profile:verification.notifications.dataIsSaved',
    }),
  );
}

function* handleClassicVerificationUpdateSuccess() {
  yield put(
    notificationsActions.showNotificationInfo({
      key: 'profile:verification.notifications.dataIsSaved',
    }),
  );
}

function* handleClassicVerificationSecondStepUpdateSuccess() {
  yield put(
    notificationsActions.showNotificationInfo({
      key: 'profile:verification.notifications.dataIsSaved',
    }),
  );
  const isKeepCurrentStep = yield select(getKeepCurrentStep);

  if (!isKeepCurrentStep) {
    yield put(openModal(VERIFICATION_AWAITING_MODAL));
  }
}

function* handleUpdatePersonalDetailsFail(action) {
  const formNames = yield select(getFormNames());
  const formName =
    formNames.find((formName) => formName === PROFILE_DETAILS_FORM) || PERSONAL_DETAILS_FORM;

  const { message: errorMessage } = get(action, 'error.data', {});

  yield put(
    stopSubmit(formName, {
      _error: errorMessage || { key: 'profile:verification.notifications.failedToSave' },
    }),
  );
}

function* handleSettingsVerificationUpdateFail() {
  yield put(
    stopSubmit(ACCOUNT_SETTINGS_FORM, {
      _error: {
        key: 'profile:verification.notifications.failedToSave',
      },
    }),
  );
}

function* handleClassicVerificationUpdateFail() {
  yield put(
    stopSubmit(IDENTITY_VERIFICATION_FORM, {
      _error: {
        key: 'profile:verification.notifications.failedToSave',
      },
    }),
  );
}

function* handleClassicVerificationSecondStepUpdateFail() {
  yield put(
    stopSubmit(ADDITIONAL_IDENTITY_VERIFICATION_FORM, {
      _error: {
        key: 'profile:verification.notifications.failedToSave',
      },
    }),
  );
}

function* handlePageOpened() {
  const user = yield select(getUser);

  yield all([
    yield put(getVerificationUserRequest(user._id)),
    yield put(geolocationActions.getCountriesRequest()),
  ]);
}

function* getLoadedCountries() {
  let countries = yield select(getFormattedCountries);
  if (countries.length === 0) {
    yield take(geolocationActions.getCountriesSuccess);
    countries = yield select(getFormattedCountries);
  }
  return countries;
}

function* initPersonalDetails() {
  let data = yield select(getPersonalDetailsVerification);
  if (data && data.phoneCountry) {
    let countries = yield call(getLoadedCountries);
    const formData = formatFormData(data, countries);
    const formNames = yield select(getFormNames());
    const formName =
      formNames.find((formName) => formName === PROFILE_DETAILS_FORM) || PERSONAL_DETAILS_FORM;
    yield put(initialize(formName, formData));
  }
  if ((data && (!data.country || !data.phoneCountry)) || !data) {
    yield put(geolocationActions.getCountryByIpRequest());
  }
}

function* initSettingsVerification() {
  let data = yield select(getSettingsVerification);
  const currency = yield select(getCurrencyByCountry);
  const leverage = yield select(defaultLeverage);
  const platform = yield select(defaultPlatform);
  const accountTypes = yield select(getUserLiveAccountTypes);
  data.accountType = data.accountType || accountTypes[0].value;
  data.leverage = data.leverage || leverage;
  data.currency = data.currency || currency;
  data.platform = data.platfrom || platform;
  data.secretQuestion = data.secretQuestion || getSecretQuestions()[0].value;
  yield put(initialize(ACCOUNT_SETTINGS_FORM, data));
}

function* initClassicVerification() {
  let data = yield select(getClassicVerification);
  yield put(initialize(IDENTITY_VERIFICATION_FORM, data));
}

function* initClassicVerificationSecondStep() {
  let data = yield select(getClassicVerificationSecondStep);
  yield put(initialize(ADDITIONAL_IDENTITY_VERIFICATION_FORM, data));
}

function* handleVerificationChanged() {
  let activeStep = yield select(getActiveStep);

  switch (activeStep) {
    case 0: {
      yield put(initializePersonalDetails());
      break;
    }
    case 1: {
      yield put(initializeSettingsVerification());
      break;
    }
    case 2:
      yield put(initializeClassicVerification());
      break;
    case 3:
      yield put(initializeClassicVerificationSecondStep());
      break;
    default:
      break;
  }
}

export default function* watchRequest() {
  yield all([
    takeEvery(updatePersonalDetailsSuccess, handleUpdatePersonalDetailsSuccess),
    takeEvery(updatePersonalDetailsFail, handleUpdatePersonalDetailsFail),
    takeEvery(
      [
        changeStepConfirmed,
        changeStepForce,
        getVerificationUserSuccess,
        updatePersonalDetailsSuccess,
        settingsVerificationUpdateSuccess,
        classicVerificationUpdateSuccess,
        classicVerificationSecondStepUpdateSuccess,
        verifyPhoneNumberSuccess,
        cancelSetupPhoneNumber,
      ],
      handleVerificationChanged,
    ),
    takeEvery(settingsVerificationUpdateSuccess, handleSettingsVerificationUpdateSuccess),
    takeEvery(settingsVerificationUpdateFail, handleSettingsVerificationUpdateFail),

    takeEvery(classicVerificationUpdateSuccess, handleClassicVerificationUpdateSuccess),
    takeEvery(classicVerificationUpdateFail, handleClassicVerificationUpdateFail),

    takeEvery(
      classicVerificationSecondStepUpdateSuccess,
      handleClassicVerificationSecondStepUpdateSuccess,
    ),
    takeEvery(
      classicVerificationSecondStepUpdateFail,
      handleClassicVerificationSecondStepUpdateFail,
    ),

    takeEvery(verificationPageOpened, handlePageOpened),
    takeEvery(initializePersonalDetails, initPersonalDetails),
    takeEvery(initializeSettingsVerification, initSettingsVerification),
    takeEvery(initializeClassicVerification, initClassicVerification),
    takeEvery(initializeClassicVerificationSecondStep, initClassicVerificationSecondStep),
    takeEvery(verifyPhoneNumberSuccess, handleVerifyPhoneNumberSuccess),
    takeEvery(verifyPhoneNumberFail, handleVerifyPhoneNumberFail),
    takeEvery(initializeClassicVerificationSecondStep, initClassicVerificationSecondStep),
  ]);
}
